import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  User,
} from "lucide-react";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { useClasses } from "../../../hooks/useClasses";
import { useStudents } from "../../../hooks/useStudents";
import { useActivities } from "../../../hooks/useActivities";
import { useEnrollments } from "../../../hooks/useEnrollments";
import { useGrades } from "../../../hooks/useGrades";

export default function StudentPerformanceManager() {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();

  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");

  const { data: classes = [] } = useClasses(token, activeProfile?.id);
  const { data: allStudents = [] } = useStudents(token, activeProfile?.id);
  const { data: allActivities = [] } = useActivities(token, activeProfile?.id);
  const { data: allEnrollments = [] } = useEnrollments(
    token,
    activeProfile?.id,
  );
  const { data: allGrades = [] } = useGrades(token);

  const { students, activities, grades, classGrades } = useMemo(() => {
    if (!selectedClassId) {
      return { students: [], activities: [], grades: [], classGrades: [] };
    }

    const classEnrollments = allEnrollments.filter(
      (e) => e.class_id === selectedClassId,
    );
    const studentIds = new Set(classEnrollments.map((e) => e.student_id));
    const classStudents = allStudents.filter((s) => studentIds.has(s.id));

    const classActivities = allActivities.filter(
      (a) => a.class_id === selectedClassId,
    );

    const activityIds = new Set(classActivities.map((a) => a.id));
    const classStudentIds = new Set(classStudents.map((s) => s.id));
    const scopedGrades = allGrades.filter(
      (g) =>
        activityIds.has(g.activity_id) && classStudentIds.has(g.student_id),
    );

    const studentGrades = selectedStudentId
      ? allGrades.filter(
          (g) =>
            g.student_id === selectedStudentId &&
            activityIds.has(g.activity_id),
        )
      : [];

    return {
      students: classStudents,
      activities: classActivities,
      grades: studentGrades,
      classGrades: scopedGrades,
    };
  }, [
    selectedClassId,
    selectedStudentId,
    allStudents,
    allActivities,
    allEnrollments,
    allGrades,
  ]);

  const metrics = useMemo(() => {
    if (!selectedStudentId || grades.length === 0) return null;

    const scores = grades
      .filter((g) => g.score !== undefined && g.score !== null)
      .map((g) => g.score as number);
    const average =
      scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

    const classAverageByActivityId = new Map<string, number>();
    const grouped: Record<string, number[]> = {};
    classGrades.forEach((g) => {
      if (g.score === undefined || g.score === null) return;
      (grouped[g.activity_id] ||= []).push(g.score as number);
    });
    Object.entries(grouped).forEach(([activityId, scores]) => {
      if (scores.length === 0) return;
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      classAverageByActivityId.set(activityId, avg);
    });

    const performanceData = grades
      .filter((g) => g.score !== undefined)
      .slice(-5)
      .map((g, i) => ({
        month: `Ativ ${i + 1}`,
        nota: g.score,
        media: classAverageByActivityId.get(g.activity_id) ?? average,
      }));

    const subjectScores: Record<string, number[]> = {};
    grades.forEach((g) => {
      const activity = activities.find((a) => a.id === g.activity_id);
      const cls = activity
        ? classes.find((c) => c.id === activity.class_id)
        : undefined;
      const subject = activity?.subject || cls?.subject;
      if (subject && g.score !== undefined) {
        const bucket = (subjectScores[subject] ??= []);
        bucket.push(g.score as number);
      }
    });

    const subjectPerformance = Object.keys(subjectScores).map((subject) => ({
      subject,
      score:
        (subjectScores[subject]?.reduce((a, b) => a + b, 0) ?? 0) /
        (subjectScores[subject]?.length ?? 1),
    }));

    return {
      average,
      attendance: Math.round(
        (grades.filter((g) => g.status === "submitted" || g.status === "graded")
          .length /
          (activities.length || 1)) *
          100,
      ),
      tasksCompleted: grades.filter(
        (g) => g.status === "submitted" || g.status === "graded",
      ).length,
      totalTasks: activities.length,
      performanceData,
      subjectPerformance,
    };
  }, [selectedStudentId, grades, classGrades, activities, classes]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        <select
          className="w-full sm:w-auto px-4 py-2 border border-(--border-color) rounded-lg bg-(--card-bg) text-(--text-primary) shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10"
          value={selectedClassId}
          onChange={(e) => {
            setSelectedClassId(e.target.value);
            setSelectedStudentId("");
          }}
        >
          <option value="">Selecione uma turma</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="w-full sm:w-auto px-4 py-2 border border-(--border-color) rounded-lg bg-(--card-bg) text-(--text-primary) shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 disabled:opacity-50"
          value={selectedStudentId}
          onChange={(e) => setSelectedStudentId(e.target.value)}
          disabled={!selectedClassId}
        >
          <option value="">Selecione um aluno</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.full_name}
            </option>
          ))}
        </select>
      </div>

      {!selectedStudentId ? (
        <div className="bg-(--card-bg) border border-(--border-color) rounded-2xl p-12 sm:p-20 text-center shadow-sm">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-(--bg-secondary) rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-8 h-8 sm:w-10 sm:h-10 text-(--text-tertiary) opacity-50" />
          </div>
          <h3 className="text-lg font-bold text-(--text-primary) mb-2">
            Resumo de Desempenho
          </h3>
          <p className="text-sm text-(--text-tertiary) max-w-sm mx-auto">
            Selecione uma turma e um aluno acima para visualizar a análise
            detalhada de desempenho e tendências.
          </p>
        </div>
      ) : metrics ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-(--card-bg) p-5 rounded-2xl border border-(--border-color) shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-(--text-tertiary)">
                  Média Geral
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-black text-(--text-primary)">
                  {metrics.average.toFixed(1)}
                </span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${metrics.average >= 7 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                >
                  {metrics.average >= 7 ? "Bom" : "Atenção"}
                </span>
              </div>
            </div>

            <div className="bg-(--card-bg) p-5 rounded-2xl border border-(--border-color) shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-(--text-tertiary)">
                  Tarefas Entregues
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-black text-(--text-primary)">
                  {metrics.tasksCompleted}/{metrics.totalTasks}
                </span>
                <span className="text-xs font-bold text-(--text-tertiary)">
                  {Math.round(
                    (metrics.tasksCompleted / (metrics.totalTasks || 1)) * 100,
                  )}
                  %
                </span>
              </div>
            </div>

            <div className="bg-(--card-bg) p-5 rounded-2xl border border-(--border-color) shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-xl">
                  <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-(--text-tertiary)">
                  Presença Est.
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-black text-(--text-primary)">
                  {metrics.attendance}%
                </span>
                <span className="text-xs font-bold text-green-600">
                  Frequência
                </span>
              </div>
            </div>

            <div className="bg-(--card-bg) p-5 rounded-2xl border border-(--border-color) shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-(--text-tertiary)">
                  Ocorrências
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-black text-(--text-primary)">
                  0
                </span>
                <span className="text-xs font-bold text-(--text-tertiary)">
                  Sem registros
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-(--card-bg) p-5 sm:p-6 rounded-2xl border border-(--border-color) shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-widest text-(--text-tertiary) mb-6">
                📈 Evolução das Notas
              </h3>
              <div className="h-[250px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={metrics.performanceData}
                    margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      opacity={0.1}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      stroke="var(--text-tertiary)"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      domain={[0, 10]}
                      stroke="var(--text-tertiary)"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "var(--card-bg)",
                        borderColor: "var(--border-color)",
                        borderRadius: "12px",
                        fontSize: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="nota"
                      stroke="var(--primary-color)"
                      strokeWidth={3}
                      dot={{
                        r: 4,
                        fill: "var(--primary-color)",
                        strokeWidth: 2,
                        stroke: "#fff",
                      }}
                      activeDot={{ r: 6 }}
                      name="Aluno"
                    />
                    <Line
                      type="monotone"
                      dataKey="media"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Média Turma"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-(--card-bg) p-5 sm:p-6 rounded-2xl border border-(--border-color) shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-widest text-(--text-tertiary) mb-6">
                📊 Desempenho por Matéria
              </h3>
              <div className="h-[250px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={metrics.subjectPerformance}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 40, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      opacity={0.1}
                      horizontal={false}
                    />
                    <XAxis type="number" domain={[0, 10]} hide />
                    <YAxis
                      dataKey="subject"
                      type="category"
                      width={80}
                      fontSize={11}
                      tick={{ fill: "var(--text-secondary)" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(0,0,0,0.02)" }}
                      contentStyle={{
                        backgroundColor: "var(--card-bg)",
                        borderColor: "var(--border-color)",
                        borderRadius: "12px",
                        fontSize: "12px",
                      }}
                    />
                    <Bar
                      dataKey="score"
                      fill="var(--primary-color)"
                      radius={[0, 4, 4, 0]}
                      barSize={16}
                      name="Média"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-(--card-bg) border border-(--border-color) rounded-2xl p-10 text-center shadow-sm">
          <p className="text-(--text-tertiary)">
            Nenhum dado encontrado para este aluno.
          </p>
        </div>
      )}
    </div>
  );
}
